import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy, HostListener } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgGlyph } from "@ng-icons/core";
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from "rxjs";
import {
  ItemBrowserConfig,
  ItemBrowserPagination,
  ItemBrowserSearchEvent,
  DateRangeFilter,
  ItemBrowserDateRangeEvent,
  ItemBrowserCardAction,
} from "./Type/ItemBrowserType";
import { formatDateNumeric } from "../../Utility/UtilityDate";

@Component({
  selector: "app-item-browser",
  standalone: true,
  imports: [CommonModule, NgGlyph],
  templateUrl: "./ItemBrowserComponent.html",
  styleUrl: "./ItemBrowserComponent.scss",
})
export class ItemBrowserComponent<T = unknown> implements OnChanges, OnInit, OnDestroy {
  @Input() data: T[] = [];
  @Input() config: ItemBrowserConfig<T> = {};
  @Input() pagination: ItemBrowserPagination = {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  };

  @Output() pageChange = new EventEmitter<{ page: number; search?: string }>();
  @Output() itemsPerPageChange = new EventEmitter<{ limit: number; search?: string }>();
  @Output() actionClick = new EventEmitter<{ action: ItemBrowserCardAction<T>; item: T }>();
  @Output() searchChange = new EventEmitter<ItemBrowserSearchEvent>();
  @Output() dateRangeChange = new EventEmitter<ItemBrowserDateRangeEvent>();

  displayedData: T[] = [];
  currentPage = 1;
  currentItemsPerPage = 10;
  searchTerm = "";
  dateRangeFilter: DateRangeFilter = {};
  showDateRangeFilter = false;
  readonly itemsPerPageOptions: number[] = [8, 12, 24, 48];
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideFilter = target.closest(".item-browser__date-range-filter-dropdown");
    const isFilterButton = target.closest(".item-browser__date-range-filter-btn");

    if (!isInsideFilter && !isFilterButton && this.showDateRangeFilter) {
      this.closeDateRangeFilter();
    }
  }

  ngOnInit(): void {
    // Set up search debounce
    this.searchSubject
      .pipe(
        debounceTime(300), // 300ms debounce
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((searchTerm) => {
        this.searchChange.emit({ term: searchTerm, page: 1 }); // Always reset to page 1 on search
      });

    // Initialize items per page from pagination
    this.currentItemsPerPage = this.pagination.limit;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this.applyPagination();
    }
    if (changes["pagination"]) {
      this.currentPage = this.pagination.page;
      this.currentItemsPerPage = this.pagination.limit;
    }
  }

  /**
   * Handles search input changes and updates the search term.
   * Triggers a debounced search event.
   * @param event - The input event from the search field.
   */
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(target.value);
  }

  /**
   * Triggers the create action if configured.
   */
  onCreateClick(): void {
    if (this.config.onCreate) {
      this.config.onCreate();
    }
  }

  /**
   * Toggles the date range filter dropdown.
   */
  toggleDateRangeFilter(): void {
    this.showDateRangeFilter = !this.showDateRangeFilter;
  }

  /**
   * Closes the date range filter dropdown.
   */
  closeDateRangeFilter(): void {
    this.showDateRangeFilter = false;
  }

  /**
   * Handles date range filter change and emits the new date range with current page.
   * @param event - The change event from the date input.
   * @param type - The type of date range input (from or to).
   */
  onDateRangeChange(event: Event, type: "from" | "to"): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (type === "from") {
      this.dateRangeFilter.from = value || undefined;
    } else {
      this.dateRangeFilter.to = value || undefined;
    }

    this.dateRangeChange.emit({
      dateRange: { ...this.dateRangeFilter },
      page: 1,
    });
  }

  /**
   * Clears the date range filter and emits empty date range.
   */
  clearDateRangeFilter(): void {
    this.dateRangeFilter = {};
    this.dateRangeChange.emit({
      dateRange: {},
      page: 1,
    });
  }

  /**
   * Checks if date range filter is currently active.
   * @returns True if either from or to date is set.
   */
  hasDateRangeFilter(): boolean {
    return !!(this.dateRangeFilter.from || this.dateRangeFilter.to);
  }

  /**
   * Applies pagination to the data.
   */
  private applyPagination(): void {
    const startIndex = (this.currentPage - 1) * this.currentItemsPerPage;
    const endIndex = startIndex + this.currentItemsPerPage;
    this.displayedData = this.data.slice(startIndex, endIndex);
  }

  /**
   * Handles page change and emits the new page number with current search term.
   * @param page - The new page number to navigate to.
   */
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.pagination.pages && page !== this.currentPage) {
      this.currentPage = page;
      this.pageChange.emit({ page, search: this.searchTerm || undefined });
    }
  }

  /**
   * Handles items per page change and emits the new limit with current search term.
   * Resets to page 1 when page size changes.
   * @param event - The change event from the items per page select.
   */
  onItemsPerPageChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const newSize = parseInt(target.value, 10);

    if (Number.isNaN(newSize) || newSize === this.pagination.limit) {
      return;
    }

    this.currentPage = 1;
    this.currentItemsPerPage = newSize;
    this.itemsPerPageChange.emit({ limit: newSize, search: this.searchTerm || undefined });
  }

  /**
   * Handles action button click and emits the action with the item.
   * @param action - The action configuration.
   * @param item - The data item the action was clicked on.
   */
  onActionClick(action: ItemBrowserCardAction<T>, item: T): void {
    if (action.handler) action.handler(item);

    this.actionClick.emit({ action, item });
  }

  /**
   * Gets the range of page numbers to display in pagination controls.
   * Shows current page ±2 pages, bounded by total pages.
   * @returns Array of page numbers to display.
   */
  getPaginationRange(): number[] {
    const range = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.pagination.pages, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Gets the end index of the current page for display in pagination info.
   * @returns The end index (capped at total items).
   */
  getEndRange(): number {
    return Math.min(this.pagination.page * this.pagination.limit, this.pagination.total);
  }

  /**
   * Gets the image URL from an item.
   * Expects imageUrl property to exist on the item.
   * @param item - The item to get the image URL from.
   * @returns The image URL or null.
   */
  getImageUrl(item: T): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (item as any).imageUrl || null;
  }

  /**
   * Gets the card title from an item using the config template or default.
   * @param item - The item to get the title from.
   * @returns The card title.
   */
  getCardTitle(item: T): string {
    if (this.config.cardTitleTemplate) {
      return String(this.config.cardTitleTemplate(item));
    }
    // Default to name or title property if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (item as any).name || (item as any).title || "Untitled";
  }

  /**
   * Gets the card subtitle from an item using the config template or default.
   * @param item - The item to get the subtitle from.
   * @returns The card subtitle or empty string.
   */
  getCardSubtitle(item: T): string {
    if (this.config.cardSubtitleTemplate) {
      return String(this.config.cardSubtitleTemplate(item));
    }
    // Default to sku or description property if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (item as any).sku || (item as any).description || "";
  }

  /**
   * Gets the image orientation for the card.
   * @returns The image orientation: "landscape" or "portrait".
   */
  getImageOrientation(): "landscape" | "portrait" {
    return this.config.imageOrientation || "landscape";
  }

  /**
   * Formats a date string to display format for the date range filter button.
   * @param dateString - The ISO date string to format.
   * @returns Formatted date string or empty string if not provided.
   */
  formatDateForDisplay(dateString?: string): string {
    if (!dateString) return "";
    return formatDateNumeric(dateString);
  }

  protected readonly Array = Array;
}
